import { Component } from '@theme/component';

/**
 * APC Store map: interactive Mapbox GL map centred on one store, matching the APA-base store-locator. Token via data-mapbox-token.
 * @typedef {Object} StoreMapRefs
 * @property {HTMLElement} map - The map canvas element.
 * @property {HTMLElement} [placeholder] - The skeleton placeholder overlay.
 * @extends {Component<StoreMapRefs>}
 */
class StoreMap extends Component {
  requiredRefs = ['map'];

  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {boolean} */
  #initialised = false;

  /** @type {*} */
  #map = null;

  connectedCallback() {
    super.connectedCallback();

    // Lazy-init the map only once it scrolls into view.
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          this.#observer?.disconnect();
          this.#init();
        }
      },
      { threshold: 0.1 },
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#observer?.disconnect();
    this.#map?.remove();

    super.disconnectedCallback();
  }

  /**
   * The Mapbox GL JS library from window.
   * @returns {*}
   */
  get #mapboxgl() {
    return /** @type {*} */ (window).mapboxgl;
  }

  /**
   * The marker position read from the data attributes.
   * @returns {{ lat: number, lng: number }}
   */
  get #position() {
    return {
      lat: Number(this.dataset.locationLatitude),
      lng: Number(this.dataset.locationLongitude),
    };
  }

  /**
   * Waits for the (deferred) Mapbox GL script to expose window.mapboxgl.
   * @returns {Promise<void>}
   */
  #whenMapboxReady() {
    if (this.#mapboxgl) return Promise.resolve();

    return new Promise((resolve, reject) => {
      let attempts = 0;
      const check = () => {
        if (this.#mapboxgl) return resolve();
        if (this.#controller.signal.aborted) return reject(new Error('aborted'));
        if (attempts++ > 200) return reject(new Error('Mapbox GL JS failed to load'));
        setTimeout(check, 50);
      };
      check();
    });
  }

  /**
   * Loads Mapbox, then creates the map, marker and floating card.
   */
  async #init() {
    if (this.#initialised) return;

    const token = this.dataset.mapboxToken;
    if (!token) {
      console.warn('apc-store-map: Mapbox access token missing (Theme settings → Mapbox).');
      return;
    }

    const position = this.#position;
    if (Number.isNaN(position.lat) || Number.isNaN(position.lng)) {
      console.warn('apc-store-map: invalid coordinates', this.dataset.locationLatitude, this.dataset.locationLongitude);
      return;
    }

    try {
      await this.#whenMapboxReady();

      const mapboxgl = this.#mapboxgl;
      mapboxgl.accessToken = token;

      /** @type {[number, number]} */
      const center = [position.lng, position.lat];

      this.#map = new mapboxgl.Map({
        container: this.refs.map,
        style: 'mapbox://styles/mapbox/light-v11',
        center,
        zoom: Number(this.dataset.zoom) || 14,
      });

      this.#map.addControl(new mapboxgl.NavigationControl());

      this.#map.on('load', () => {
        this.#addMarker(mapboxgl, center);
        this.#hidePlaceholder();
        this.#offsetCenter(center);
        this.#initialised = true;
      });
    } catch (error) {
      console.error('apc-store-map: failed to initialise map', error);
      // Don't leave the skeleton shimmering forever over an empty frame.
      this.#hidePlaceholder();
    }
  }

  /**
   * Adds a base-style teardrop pin and, when present, the server-rendered card floating above it.
   * @param {*} mapboxgl - The Mapbox GL JS library.
   * @param {[number, number]} center - The [lng, lat] position.
   */
  #addMarker(mapboxgl, center) {
    new mapboxgl.Marker({ color: this.dataset.pinColor || '#E81E75', anchor: 'bottom' })
      .setLngLat(center)
      .addTo(this.#map);

    // Attach the server-rendered popup as a second marker so it tracks the pin on pan/zoom.
    const popup = this.querySelector('.apc-store-map__popup');
    if (popup) {
      const wrapper = document.createElement('div');
      wrapper.className = 'apc-store-map__marker';
      wrapper.appendChild(popup);
      new mapboxgl.Marker({ element: wrapper, anchor: 'bottom', offset: [0, -44] }).setLngLat(center).addTo(this.#map);
    }
  }

  /**
   * Shifts the centre so the pin sits lower in the frame, leaving room for the card above.
   * @param {[number, number]} center - The [lng, lat] position.
   */
  #offsetCenter(center) {
    if (!this.#map) return;

    const part = window.matchMedia('(width < 64rem)').matches ? 4 : 5;
    const offsetY = this.refs.map.clientHeight / part;
    this.#map.easeTo({ center, offset: [0, offsetY], duration: 0 });
  }

  /**
   * Hides the skeleton placeholder once the map is ready.
   */
  #hidePlaceholder() {
    this.refs.placeholder?.classList.add('apc-store-map__placeholder--hidden');
  }
}

if (!customElements.get('apc-store-map')) {
  customElements.define('apc-store-map', StoreMap);
}
